import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { Divider } from '@mui/material';
import { IconButton } from '@mui/material';
import { generateTestId } from '@vs/functions/generate-test-id';
import { onEnterTriggerClick } from '@vs/functions/on-enter-trigger-click';
import { PortalLayoutMode } from '@vs/types';
import { useEnv } from '@vs/utils/envConfig';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';

export type PortalHeaderProps = {
  onBurgerIconClick?: () => void;
  onLogoClick?: () => void;
  showMobileBurgerIcon?: boolean;
  shouldElevateOnScroll?: boolean;
  shouldFixed?: boolean;
  actions?: React.ReactNode;
  avatarMenu?: React.ReactNode;
  othersCTA?: React.ReactNode;
  subLogo?: React.ReactNode;
  mode?: PortalLayoutMode;
  ariaLabel?: {
    logo?: string;
  };
};

export function PortalHeader({
  onBurgerIconClick = () => {},
  onLogoClick = () => {},
  showMobileBurgerIcon = true,
  shouldElevateOnScroll = false,
  shouldFixed = false,
  actions = null,
  avatarMenu = null,
  othersCTA = null,
  subLogo = null,
  mode = 'app',
  ariaLabel,
}: PortalHeaderProps) {
  const env = useEnv();
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    if (shouldElevateOnScroll) {
      const handleScroll = () => {
        setHasScrolled(window.scrollY > 0);
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [shouldElevateOnScroll]);

  return (
    <header
      className={classNames(
        'z-40 flex h-14 items-center justify-between px-4 py-2 border-b transition-all md:h-16 md:py-3 lg:px-6 xl:px-8',
        { 'bg-vs-base-100 border-vs-neut-300': mode === 'app' },
        { 'bg-vs-base-300 border-b-transparent': mode === 'doc' },
        {
          shadow: shouldElevateOnScroll && hasScrolled,
          'fixed left-0 right-0 top-0 w-full': shouldFixed,
        }
      )}
    >
      <div className="flex items-center">
        {showMobileBurgerIcon && (
          <IconButton
            className="hover:bg-vs-neut-200 me-2 cursor-pointer rounded p-2 transition-colors lg:hidden"
            onClick={onBurgerIconClick}
          >
            <MenuRoundedIcon className="text-vs-base-content h-[22px] w-[22px]" />
          </IconButton>
        )}
        <div
          {...generateTestId('btn-like', ['header', 'vs-logo'])}
          className="flex items-center cursor-pointer focus:outline focus:outline-offset-4 focus:outline-2 focus:outline-vs-secondary rounded"
          onClick={onLogoClick}
          onKeyDown={onEnterTriggerClick}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel?.logo}
        >
          <img
            src={`${env.assets.vs}/images/${subLogo ? 'vs-logo' : 'vs-logo-bird'}.png`}
            alt="Viewsonic"
            className="h-5 md:h-7"
          />
          {Boolean(subLogo) && (
            <div className="flex items-center">
              <Divider
                orientation="vertical"
                className="border-vs-neut-1000 mx-3 h-4 sm:h-5 sm:mx-4"
              />
              {subLogo}
            </div>
          )}
        </div>
      </div>
      {actions}
      <div className="flex items-center">
        {othersCTA}
        {avatarMenu}
      </div>
    </header>
  );
}

export default PortalHeader;

export function PortalHeaderAppSubTitle({ children }: { children: ReactNode }) {
  return (
    <span className="font-vs-portal text-xs md:text-xl text-vs-neut-1000">
      {children}
    </span>
  );
}
